import { memo } from 'react';
import type { ImgHTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAuthorizedImage } from './useAuthorizedImage';
import type {
  AuthorizedImageLoadPriority,
  AuthorizedImageProps,
} from './AuthorizedImage.types';

const DEFAULT_SKELETON = 'animate-pulse bg-gray-200';
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const LAZY_IMAGE_ROOT_MARGIN = '800px 0px';

/**
 * Decyduje czy `url` jest chronionym endpointem API (wymaga `Authorization`).
 * Lokalne assety Vite (`/src/...`, `/assets/...`), `data:`/`blob:` i zewnętrzne
 * URL-e renderujemy zwykłym `<img>`.
 */
const isAuthorizedTarget = (url: string): boolean => {
  if (url.startsWith('data:') || url.startsWith('blob:')) return false;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return Boolean(API_BASE_URL) && url.startsWith(API_BASE_URL);
  }
  return url.startsWith('/api/');
};

const AuthorizedImageComponent = ({
  src,
  alt,
  fallbackSrc,
  className,
  skeletonClassName,
  loadPriority = 'normal',
  ...imgProps
}: AuthorizedImageProps) => {
  // URL spoza naszego API — zwykły `<img>` (storybook, lokalne assety, fallbacki).
  if (src && !isAuthorizedTarget(src)) {
    return <img src={src} alt={alt} className={className} {...imgProps} />;
  }

  return (
    <AuthorizedImageInner
      src={src}
      alt={alt}
      fallbackSrc={fallbackSrc}
      className={className}
      skeletonClassName={skeletonClassName}
      loadPriority={loadPriority}
      imgProps={imgProps}
    />
  );
};

interface InnerProps extends Omit<
  AuthorizedImageProps,
  'loadPriority' | 'src'
> {
  src: string | undefined;
  loadPriority: AuthorizedImageLoadPriority;
  imgProps: Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'alt' | 'className'
  >;
}

const AuthorizedImageInner = ({
  src,
  alt,
  fallbackSrc,
  className,
  skeletonClassName,
  loadPriority,
  imgProps,
}: InnerProps) => {
  const lazyAnchorRef = useRef<HTMLDivElement | null>(null);
  const shouldLazyLoad = imgProps.loading !== 'eager';
  const [loadableSrc, setLoadableSrc] = useState<string | undefined>(() =>
    shouldLazyLoad ? undefined : src,
  );
  const imageUrl = loadableSrc === src ? loadableSrc : undefined;
  const isWaitingForLazyLoad = shouldLazyLoad && imageUrl !== src;
  const { src: objectUrl, status } = useAuthorizedImage(imageUrl, {
    priority: loadPriority,
  });

  useEffect(() => {
    if (!src) {
      setLoadableSrc(undefined);
      return;
    }

    if (!shouldLazyLoad) {
      setLoadableSrc(src);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setLoadableSrc(src);
      return;
    }

    setLoadableSrc(undefined);

    const observedElement = lazyAnchorRef.current;
    if (!observedElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isNearViewport = entries.some(
          (entry) => entry.isIntersecting || entry.intersectionRatio > 0,
        );

        if (!isNearViewport) {
          return;
        }

        setLoadableSrc(src);
        observer.disconnect();
      },
      {
        rootMargin: LAZY_IMAGE_ROOT_MARGIN,
        threshold: 0.01,
      },
    );

    observer.observe(observedElement);

    return () => {
      observer.disconnect();
    };
  }, [src, shouldLazyLoad]);

  if (isWaitingForLazyLoad || status === 'loading' || status === 'idle') {
    return (
      <div
        ref={lazyAnchorRef}
        role="img"
        aria-label={alt}
        aria-busy={status === 'loading'}
        className={clsx(className, skeletonClassName ?? DEFAULT_SKELETON)}
      />
    );
  }

  if (status === 'error' || !objectUrl) {
    if (fallbackSrc) {
      return (
        <img src={fallbackSrc} alt={alt} className={className} {...imgProps} />
      );
    }
    return (
      <div
        role="img"
        aria-label={alt}
        className={clsx(className, 'bg-gray-100')}
      />
    );
  }

  return <img src={objectUrl} alt={alt} className={className} {...imgProps} />;
};

export const AuthorizedImage = memo(AuthorizedImageComponent);
